import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Zap, Clock, Link2, EyeOff, FileDown, LayoutGrid } from "lucide-react";
import { motion } from "framer-motion";

const featureChips = [
  { icon: Clock, label: "2-Min Build" },
  { icon: Link2, label: "Saved Links" },
  { icon: EyeOff, label: "Toggle Sections" },
  { icon: FileDown, label: "Smart Downloads" },
  { icon: LayoutGrid, label: "Multiple Templates" },
];

const HeroSection = () => {
  const navigate = useNavigate();

  return (
    <section className="relative min-h-[92vh] flex items-center overflow-hidden">
      {/* Full-width teal gradient mesh */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute top-[-10%] left-[-5%] w-[60%] h-[70%] rounded-full bg-primary/10 blur-[120px]" />
        <div className="absolute bottom-[-10%] right-[-5%] w-[50%] h-[60%] rounded-full bg-accent/8 blur-[100px]" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[80%] h-[80%] rounded-full bg-primary/3 blur-[150px]" />
      </div>

      <div className="w-full px-6 sm:px-10 lg:px-16 pt-28 pb-16">
        <div className="max-w-5xl mx-auto">
          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="flex justify-center mb-8"
          >
            <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary/10 border border-primary/20 text-sm font-semibold text-primary">
              <Zap className="w-4 h-4" />
              Not Just Another Resume Builder
              <span className="w-2 h-2 rounded-full bg-success animate-pulse" />
            </div>
          </motion.div>

          {/* Headline */}
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-4xl sm:text-5xl lg:text-7xl font-heading font-extrabold text-foreground leading-[1.08] mb-6 tracking-tight text-center"
          >
            Apply Faster.{" "}
            <span className="relative inline-block">
              <span className="text-gradient">Land More.</span>
              <svg className="absolute -bottom-2 left-0 w-full" viewBox="0 0 300 12" fill="none">
                <path d="M2 8C50 2 100 2 150 6C200 10 250 4 298 8" stroke="hsl(var(--primary))" strokeWidth="3" strokeLinecap="round" opacity="0.6" />
              </svg>
            </span>
          </motion.h1>

          {/* Subheadline */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-lg sm:text-xl text-muted-foreground mb-8 max-w-2xl mx-auto leading-relaxed text-center"
          >
            Resume Flow AI is built for speed. Copy-paste your content, save your links, 
            toggle sections, rename downloads instantly, and apply to{" "}
            <span className="text-foreground font-medium">dozens of jobs in minutes</span>, 
            not hours.
          </motion.p>

          {/* Feature chips carousel */}
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="flex flex-wrap items-center justify-center gap-3 mb-10"
          >
            {featureChips.map((chip, i) => (
              <div
                key={i}
                className="flex items-center gap-2 px-4 py-2 rounded-full bg-card border border-border/60 text-sm text-foreground font-medium shadow-sm"
              >
                <chip.icon className="w-4 h-4 text-primary" />
                {chip.label}
              </div>
            ))}
          </motion.div>

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16"
          >
            <Button
              size="lg"
              onClick={() => navigate("/auth")}
              className="gradient-button text-primary-foreground text-lg px-10 py-7 rounded-2xl hover:opacity-90 transition-all duration-300 shadow-xl hover:shadow-2xl hover:-translate-y-0.5 group"
            >
              Start Building for Free
              <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
            <Button
              variant="outline"
              size="lg"
              onClick={() => navigate("/about")}
              className="text-lg px-8 py-7 rounded-2xl border-2 bg-card/50 hover:bg-card/80 transition-all duration-300"
            >
              Learn More
            </Button>
          </motion.div>

          {/* Stats */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-6"
          >
            {[
              { value: "90%+", label: "ATS Pass Rate" },
              { value: "<2min", label: "Average Build Time" },
              { value: "Free", label: "No Credit Card" },
              { value: "100+", label: "Jobs Applied Weekly" },
              
            ].map((stat, i) => (
              <div key={i} className="text-center p-4 rounded-2xl bg-card/60 border border-border/50">
                <div className="text-2xl sm:text-3xl font-bold text-gradient">{stat.value}</div>
                <div className="text-xs sm:text-sm text-muted-foreground mt-1">{stat.label}</div>
              </div>
            ))}
          </motion.div>
        </div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
      >
        <div className="w-6 h-10 rounded-full border-2 border-muted-foreground/30 flex justify-center pt-2">
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 1.5, repeat: Infinity }}
            className="w-1.5 h-1.5 rounded-full bg-primary"
          />
        </div>
      </motion.div>
    </section>
  );
};

export default HeroSection;
